import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { fetchCmsCollection, getOptimizedImageUrl } from "../lib/cms";
import ContactForm from "../components/ContactForm";
import QuickContactBanner from "../components/QuickContactBanner";

export default function ServicesIndexPage() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    async function loadServices() {
      const data = await fetchCmsCollection("services", { sort: "order", order: "asc" });
      setServices(data.items || []);
    }
    loadServices();
  }, []);

  const servicesList = [
    {
      title: "Nyt tag & tagrenovering",
      slug: "/nyt-tag-ribe",
      icon: "fa-solid fa-house-chimney",
      desc: "Transformer dit hjem både æstetisk og funktionelt. Det beskytter ikke blot mod vejrets påvirkninger, men forbedrer også energiforbruget.",
      image: "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/nyt-tegltag-paa-villa-400x284.jpg",
      points: ["Tegltag, betontagsten, stål og tagpap", "Gennemgang af undertag og konstruktion", "Forbedring af isolering og energibesparelser", "Håndtering og sanering af asbesttage"]
    },
    {
      title: "Nye vinduer og døre",
      slug: "/nye-vinduer-og-doere-ribe",
      icon: "fa-solid fa-door-open",
      desc: "En professionel installation eller udskiftning sikrer optimal isolering og sikkerhed, samtidig med at det giver et friskt udtryk til dit hjem.",
      image: "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/klassisk-vinterhave-med-havestue.jpg",
      points: ["Træ, træ/aluminium og PVC/plast", "Energiruder og termoruder efter mål", "Dannebrogs-, bondehus- og Velux-vinduer", "Hoveddøre, terrassedøre og skydedøre"]
    },
    {
      title: "Renovering af hus",
      slug: "/renovering-af-hus",
      icon: "fa-solid fa-hammer",
      desc: "Skræddersyede tømrerløsninger der kombinerer funktionalitet med æstetik og sikrer at dit byggeri opfylder både dine drømme og behov.",
      image: "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/facade-renovering-stillads-tomrer-ribe-400x284.jpg",
      points: ["Køkkenrenovering med snedkerdetaljer", "Badeværelsesopdatering og koordinering", "Totalentreprise og hovedentreprise", "Facadearbejde og indvendig ombygning"]
    },
    {
      title: "Tilbygning & Vinterhave",
      slug: "/tilbygning-vinterhave",
      icon: "fa-solid fa-tree-city",
      desc: "Udvid boligen med en lys og harmonisk tilbygning eller vinterhave, skræddersyet til huset.",
      image: "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/vinterhave-tilbygning-med-lys-og-glas-400x284.jpg",
      points: ["Masser af dagslys og store glaspartier", "Godkendte konstruktioner og optimal isolering", "Individuel tilpasning til husets arkitektur", "Komplet udførelse fra sokkel til tag"]
    },
    {
      title: "Snedkerarbejde & Køkken",
      slug: "/snedkerarbejde",
      icon: "fa-solid fa-ruler-combined",
      desc: "Eksklusive snedkerløsninger og køkkener i massivt træ tilpasset boligens rum og behov.",
      image: "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/snedkerlavet-egetraeskoekken-med-teglgulv-400x284.jpg",
      points: ["Håndbyggede snedkerkøkkener i massiv eg", "Specialfremstillede bordplader og køkkenøer", "Indbyggede skabe og reoler", "Unikke trædetaljer og præcist håndværk"]
    }
  ];

  return (
    <>
      <SEO
        title="Vi tilbyder - Tømrermester Jacob"
        description="Vi tilbyder: Tømrerarbejde, Nyt tag, Vinduer og døre, Renovering, Tilbygninger og Snedkerarbejde i Ribe og omegn."
        ogTitle="Vi tilbyder - Tømrermester Jacob"
        ogDescription="Vi tilbyder: Tømrerarbejde, Nyt tag, Vinduer og døre."
      />

      {/* Hero */}
      <section className="bg-stone-100/60 border-b border-stone-200 py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-red-100 text-[#ce0611] text-xs font-semibold uppercase tracking-wider mb-4">
            <i className="fa-solid fa-layer-group text-[#ce0611]" aria-hidden="true"></i>
            <span>Håndværksydelser</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 mb-4">
            Vi tilbyder
          </h1>
          <p className="text-base sm:text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Professionelt tømrer- og snedkerarbejde udført med faglig stolthed. Udforsk vores ydelser nedenfor og læs mere om mulighederne for din bolig.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {servicesList.map((srv, idx) => (
              <div
                key={srv.slug}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center p-6 sm:p-8 rounded-2xl border border-stone-200 bg-stone-50/50 ${
                  idx % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={`lg:col-span-6 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative rounded-xl overflow-hidden aspect-[16/10] bg-stone-200 shadow-sm border border-stone-200">
                    <img
                      src={getOptimizedImageUrl(srv.image, { width: 800 })}
                      alt={srv.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className={`lg:col-span-6 space-y-4 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className="inline-flex items-center space-x-2 text-xs font-semibold uppercase tracking-wider text-[#ce0611]">
                    <i className={srv.icon} aria-hidden="true"></i>
                    <span>Fagområde</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                    {srv.title}
                  </h2>
                  <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                    {srv.desc}
                  </p>

                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
                    {srv.points.map((pt, i) => (
                      <li key={i} className="flex items-start text-xs sm:text-sm text-stone-700">
                        <i className="fa-solid fa-check text-[#ce0611] mr-2 mt-0.5" aria-hidden="true"></i>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-4 flex items-center space-x-4">
                    <Link
                      to={srv.slug}
                      className="inline-flex items-center px-6 py-2.5 rounded-full bg-[#ce0611] hover:bg-[#b0040e] text-white font-medium text-sm shadow-xs transition-colors"
                    >
                      <span>Læs mere om {srv.title.toLowerCase()}</span>
                      <i className="fa-solid fa-arrow-right ml-2 text-xs" aria-hidden="true"></i>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <QuickContactBanner />

      {/* Contact Section */}
      <section className="py-16 sm:py-20 bg-stone-50 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactForm
            title="Har du spørgsmål til en af vores ydelser?"
            subtitle="Udfyld formularen, så kontakter vi dig med faglig vejledning og et uforpligtende tilbud."
          />
        </div>
      </section>
    </>
  );
}
